/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Settings tab Settings Profile
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Sergey Mysov
 * @author Ilya Baranchikov
 * @version 1.26.0
 * @since 1.3.0
 */

import { useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { LinkBlock } from '@components/common';
import { makeSelectAuthSSO } from '@modules/app/appSelectors';
import { EModeSSO, IAuthSSO } from '@modules/app/appTypes';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageSettings,
} from '@modules/internationalization/intSelectors';
import ICommonTranslation from '@modules/internationalization/types/common';
import { IPageSettingsTranslation } from '@modules/internationalization/types/pages/pageSettings';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';
import { IRootReducer } from '@reducers/rootReducer';

import { PageSettingsContentStyled } from '../../styled/PageSettingsStyled';
import {
    settingsProfileInitAction,
    settingsProfileResetParamsAction,
    settingsProfileSaveCommonAction,
    settingsProfileSetParamComponentsAction,
    settingsProfileSetParamContactsAction,
    settingsProfileSetParamPasswordAction,
    settingsProfileSetParamPreferredLKAction,
    settingsProfileSetParamQuestionAction,
} from '../settingsProfileActions';
import { ESettingsProfileComponents } from '../settingsProfileConstants';
import {
    makeSelectSettingsProfileIsCommonParamsUpdated,
    makeSelectSettingsProfileIsCommonParamsValid,
    makeSelectSettingsProfileIsPreferredLKParamsUpdated,
    makeSelectSettingsProfileIsRecoveryAvailable,
    makeSelectSettingsProfileMetaPassword,
    makeSelectSettingsProfileMetaQuestion,
    makeSelectSettingsProfileParamsContacts,
    makeSelectSettingsProfileParamsHints,
    makeSelectSettingsProfileParamsOnboarding,
    makeSelectSettingsProfileParamsPassword,
    makeSelectSettingsProfileParamsPreferredLK,
    makeSelectSettingsProfileParamsQuestion,
} from '../settingsProfileSelectors';
import {
    ISettingsProfileMetaPassword,
    ISettingsProfileMetaQuestion,
    ISettingsProfileParamsContacts,
    ISettingsProfileParamsHints,
    ISettingsProfileParamsOnboarding,
    ISettingsProfileParamsPassword,
    ISettingsProfileParamsQuestion,
    ISettingsProfilePreferredLK,
    ISettingsProfileResetParamsActionPayload,
    TSettingsProfileSetParamComponentActionPayload,
    TSettingsProfileSetParamContactsActionPayload,
    TSettingsProfileSetParamPasswordActionPayload,
    TSettingsProfileSetParamPreferredLKActionPayload,
    TSettingsProfileSetParamQuestionActionPayload,
} from '../settingsProfileTypes';
import {
    SettingsProfileColumnStyled,
    SettingsProfileComponentContainerStyled,
    SettingsProfileControlStyled,
} from '../styled/SettingsProfileStyled';
import { SettingsProfileContacts } from './SettingsProfileContacts';
import { SettingsProfileHints } from './SettingsProfileHints';
import { SettingsProfilePassword } from './SettingsProfilePassword';
import { SettingsProfilePreferredLK } from './SettingsProfilePreferredLK';
import { SettingsProfileQuestion } from './SettingsProfileQuestion';

export type TProfileProps = IConnectedProps & IDispatchProps;

export const SettingsProfile = (props: TProfileProps) => {

    useEffect(() => {
        props.settingsProfileInit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PageSettingsContentStyled data-module="settingsProfile">
            <SettingsProfileColumnStyled>
                {props.paramsPreferredLK.preferred && (
                    <SettingsProfileComponentContainerStyled data-component="preferredLK">
                        <SettingsProfilePreferredLK
                            params={props.paramsPreferredLK}
                            setParam={props.setParamPreferredLK}
                            translation={props.settingsTranslation}
                        />
                    </SettingsProfileComponentContainerStyled>
                )}

                <SettingsProfileComponentContainerStyled data-component="profileContacts">
                    <SettingsProfileContacts
                        commonTranslation={props.commonTranslation}
                        params={props.paramsContacts}
                        setParam={props.setParamContacts}
                        settingsTranslation={props.settingsTranslation}
                    />
                </SettingsProfileComponentContainerStyled>

                <SettingsProfileComponentContainerStyled data-component="profileHints">
                    <SettingsProfileHints
                        params={{ ...props.paramsHints, ...props.paramsOnboarding }}
                        setParam={props.setParamHints}
                        translation={props.settingsTranslation}
                    />
                </SettingsProfileComponentContainerStyled>

                <SettingsProfileControlStyled data-component="profileControlPanel">
                    <ButtonServiceUserDenied
                        dataName="saveChangesButton"
                        disabled={
                            !(
                                (props.isCommonParamsUpdated || props.isPreferredLKParamsUpdated) &&
                                props.isCommonParamsValid
                            )
                        }
                        kind="mediumNormal"
                        type="submit"
                        onClick={props.saveChanges}
                    >
                        {props.settingsTranslation.profile.submit}
                    </ButtonServiceUserDenied>
                    <LinkBlock
                        dataName="cancelButton"
                        disabled={!(props.isCommonParamsUpdated || props.isPreferredLKParamsUpdated)}
                        onClick={() =>
                            props.resetParams({
                                params: [
                                    ESettingsProfileComponents.CONTACTS,
                                    ESettingsProfileComponents.PREFERRED_LK,
                                    ESettingsProfileComponents.HINTS,
                                    ESettingsProfileComponents.ONBOARDING,
                                ],
                            })
                        }
                    >
                        {props.settingsTranslation.profile.cancel}
                    </LinkBlock>
                </SettingsProfileControlStyled>

                {props.isRecoveryAvailable && (
                    <SettingsProfileComponentContainerStyled data-component="profileQuestion">
                        <SettingsProfileQuestion
                            meta={props.metaQuestion}
                            openModal={props.openModal}
                            resetParams={props.resetParams}
                            settingsTranslation={props.settingsTranslation}
                        />
                    </SettingsProfileComponentContainerStyled>
                )}

                <SettingsProfileComponentContainerStyled data-component="profilePassword">
                    <SettingsProfilePassword
                        externalUrl={props.authSSO.mode === EModeSSO.sso ? props.authSSO.changePassword : ''}
                        meta={props.metaPassword}
                        openModal={props.openModal}
                        resetParams={props.resetParams}
                        settingsTranslation={props.settingsTranslation}
                    />
                </SettingsProfileComponentContainerStyled>
            </SettingsProfileColumnStyled>
        </PageSettingsContentStyled>
    );
};

export interface IConnectedProps {
    authSSO: IAuthSSO;
    commonTranslation: ICommonTranslation;
    isPreferredLKParamsUpdated: boolean;
    isCommonParamsUpdated: boolean;
    isCommonParamsValid: boolean;
    isRecoveryAvailable: boolean;
    metaPassword: ISettingsProfileMetaPassword;
    metaQuestion: ISettingsProfileMetaQuestion;
    paramsContacts: ISettingsProfileParamsContacts;
    paramsPreferredLK: ISettingsProfilePreferredLK;
    paramsHints: ISettingsProfileParamsHints;
    paramsOnboarding: ISettingsProfileParamsOnboarding;
    paramsPassword: ISettingsProfileParamsPassword;
    paramsQuestion: ISettingsProfileParamsQuestion;
    settingsTranslation: IPageSettingsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    authSSO: makeSelectAuthSSO(),
    commonTranslation: makeSelectTranslationCommon(),
    isPreferredLKParamsUpdated: makeSelectSettingsProfileIsPreferredLKParamsUpdated(),
    isCommonParamsUpdated: makeSelectSettingsProfileIsCommonParamsUpdated(),
    isCommonParamsValid: makeSelectSettingsProfileIsCommonParamsValid(),
    isRecoveryAvailable: makeSelectSettingsProfileIsRecoveryAvailable(),
    metaPassword: makeSelectSettingsProfileMetaPassword(),
    metaQuestion: makeSelectSettingsProfileMetaQuestion(),
    paramsContacts: makeSelectSettingsProfileParamsContacts(),
    paramsPreferredLK: makeSelectSettingsProfileParamsPreferredLK(),
    paramsHints: makeSelectSettingsProfileParamsHints(),
    paramsOnboarding: makeSelectSettingsProfileParamsOnboarding(),
    paramsPassword: makeSelectSettingsProfileParamsPassword(),
    paramsQuestion: makeSelectSettingsProfileParamsQuestion(),
    settingsTranslation: makeSelectTranslationPageSettings(),
});

export interface IDispatchProps {
    openModal: (payload: IModalOpenPayload) => void;
    resetParams: (payload: ISettingsProfileResetParamsActionPayload) => void;
    setParamContacts: (payload: TSettingsProfileSetParamContactsActionPayload) => void;
    setParamPreferredLK: (payload: TSettingsProfileSetParamPreferredLKActionPayload) => void;
    setParamHints: (payload: TSettingsProfileSetParamComponentActionPayload) => void;
    setParamPassword: (payload: TSettingsProfileSetParamPasswordActionPayload) => void;
    setParamQuestion: (payload: TSettingsProfileSetParamQuestionActionPayload) => void;
    saveChanges: () => void;
    settingsProfileInit: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    openModal: (payload) => dispatch(modalOpen(payload)),
    resetParams: (payload) => dispatch(settingsProfileResetParamsAction(payload)),
    saveChanges: () => dispatch(settingsProfileSaveCommonAction()),
    setParamContacts: (payload) => dispatch(settingsProfileSetParamContactsAction(payload)),
    setParamPreferredLK: (payload) => dispatch(settingsProfileSetParamPreferredLKAction(payload)),
    setParamHints: (payload) => dispatch(settingsProfileSetParamComponentsAction(payload)),
    setParamPassword: (payload) => dispatch(settingsProfileSetParamPasswordAction(payload)),
    setParamQuestion: (payload) => dispatch(settingsProfileSetParamQuestionAction(payload)),
    settingsProfileInit: () => dispatch(settingsProfileInitAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsProfile);
